import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Search, Store as StoreIcon } from 'lucide-react';
import { STORE_LAYOUT_LABELS } from '@bp/shared';
import { api } from '@/lib/api';
import type { Store } from '@/lib/types';
import { fmtDate } from '@/lib/utils';
import { Badge, Card, Empty, ErrorNote, Input, Loading, PageHeader } from '@/components/ui';
import { ScoreBadge, StoreStatusBadge } from '@/components/badges';

export function StoresPage() {
  const q = useQuery({ queryKey: ['stores'], queryFn: () => api.get<Store[]>('/api/stores') });
  const [search, setSearch] = useState('');
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorNote error={q.error} />;
  const term = search.trim().toLowerCase();
  const stores = q.data!.filter((s) => !term || [s.name, s.store_number, s.location, s.region].some((v) => v.toLowerCase().includes(term)));
  return (
    <>
      <PageHeader title="Stores" subtitle="Pick a store to start an audit, see its tasks and history." />
      <div className="relative mb-4 max-w-sm">
        <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-fg" />
        <Input className="pl-9" placeholder="Search by name, number or region" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      {stores.length === 0 ? (
        <Card><Empty icon={<StoreIcon className="size-8" />} title={q.data!.length ? 'No stores match.' : 'No stores assigned to you yet.'}>{!q.data!.length && 'Ask an admin to assign you to a store.'}</Empty></Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {stores.map((s) => (
            <Link key={s.id} to={`/stores/${s.id}`}>
              <Card className="h-full p-4 transition-shadow hover:shadow-md">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate font-semibold">{s.name}</p>
                    <p className="text-sm text-muted-fg">#{s.store_number}{s.location && ` · ${s.location}`}</p>
                  </div>
                  <ScoreBadge score={s.metrics.latest_score} className="text-sm" />
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  <StoreStatusBadge status={s.metrics.status} />
                  {s.layout !== 'standard' && <Badge>{STORE_LAYOUT_LABELS[s.layout]}</Badge>}
                  {s.metrics.overdue_action_items > 0 && <Badge tone="fail">{s.metrics.overdue_action_items} overdue</Badge>}
                </div>
                <p className="mt-3 text-xs text-muted-fg">Last official audit {fmtDate(s.metrics.last_audit_date)} · {s.metrics.open_action_items} open tasks</p>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}
